import {
  computed,
  inject,
  onActivated,
  onDeactivated,
  onScopeDispose,
  provide,
  ref,
  shallowRef,
  toValue,
  type ComputedRef,
  type InjectionKey,
  type MaybeRefOrGetter
} from 'vue';

export type OpenAppHandler = () => boolean | void;

export interface IAppShareLayoutProps {
  title?: string;
  subTitle?: string;
  describe?: string;
  trackData?: unknown;
  showShareBtn?: boolean;
  hasHeight?: boolean;
  dart?: boolean;
  maxWidth?: string;
}

type AppShareLayoutPropSources = {
  [Key in keyof IAppShareLayoutProps]?: MaybeRefOrGetter<IAppShareLayoutProps[Key]>;
};

export interface IUseAppShareLayoutOptions extends AppShareLayoutPropSources {
  openApp?: OpenAppHandler;
}

interface IAppShareLayoutRegistration {
  id: symbol;
  options: IUseAppShareLayoutOptions;
}

interface IAppShareLayoutContext {
  register: (options: IUseAppShareLayoutOptions) => () => void;
}

// AppShareLayout 与后代业务页面共享的唯一注入键，避免字符串 key 冲突。
const APP_SHARE_LAYOUT_KEY: InjectionKey<IAppShareLayoutContext> = Symbol('appShareLayout');

/**
 * 在 AppShareLayout 中创建分享布局上下文。
 * 负责接收业务页面配置，并整理成最终传给 SharedShareLayout 的 props。
 */
export function provideAppShareLayout(defaultOpenApp: OpenAppHandler) {
  // 使用注册栈支持多个存活组件覆盖配置；最后注册的配置优先生效。
  const registrations = shallowRef<IAppShareLayoutRegistration[]>([]);
  // 兼容尚未迁移到 useAppShareLayout({ dart }) 的旧页面。
  const legacyDartTheme = ref(false);

  const activeOptions = computed(() => registrations.value[registrations.value.length - 1]?.options);

  const shareLayoutProps: ComputedRef<IAppShareLayoutProps> = computed(() => {
    const options = activeOptions.value;

    // toValue 同时支持普通值、ref/computed 和 getter，默认值统一在布局配置层补齐。
    return {
      title: toValue(options?.title) || '梦游社App',
      subTitle: toValue(options?.subTitle),
      describe: toValue(options?.describe) || '圈住你的热爱',
      trackData: toValue(options?.trackData),
      showShareBtn: toValue(options?.showShareBtn) ?? true,
      hasHeight: toValue(options?.hasHeight) ?? true,
      dart: toValue(options?.dart) ?? legacyDartTheme.value,
      maxWidth: toValue(options?.maxWidth) ?? '100%'
    };
  });

  const register = (options: IUseAppShareLayoutOptions) => {
    // 每次注册使用独立 Symbol，确保清理函数只删除自己创建的记录。
    const registration: IAppShareLayoutRegistration = {
      id: Symbol('appShareLayoutRegistration'),
      options
    };

    registrations.value = [...registrations.value, registration];

    return () => {
      registrations.value = registrations.value.filter(({ id }) => id !== registration.id);
    };
  };

  // 业务页面未提供特殊处理器时，统一执行布局的默认打开 App 行为。
  const openApp: OpenAppHandler = () => {
    const handler = activeOptions.value?.openApp;
    return handler ? handler() : defaultOpenApp();
  };

  // register 仅通过类型化上下文暴露；SharedShareLayout 继续消费兼容的字符串注入项。
  provide(APP_SHARE_LAYOUT_KEY, { register });
  provide('openApp', openApp);

  return {
    shareLayoutProps,
    openApp,
  };
}

/**
 * 在分享业务页面中声明当前页面的布局配置。
 * 页面卸载或 KeepAlive 失活时自动撤销，重新激活时恢复注册。
 */
export function useAppShareLayout(options: IUseAppShareLayoutOptions = {}) {
  const context = inject(APP_SHARE_LAYOUT_KEY);

  if (!context) {
    throw new Error('useAppShareLayout must be used within AppShareLayout');
  }

  let unregister: (() => void) | undefined = context.register(options);

  const cleanup = () => {
    unregister?.();
    unregister = undefined;
  };

  const register = () => {
    // 激活前先清理旧记录，避免 KeepAlive 页面重复入栈。
    cleanup();
    unregister = context.register(options);
  };

  onActivated(register);
  onDeactivated(cleanup);
  onScopeDispose(cleanup);
}
